// ============================================================================
// JARVISTRAVEL - FATIGUE INDEX FORMULA (JAR-1678)
// A port of core's services/trip-core/internal/fatigue/index.go, which is the
// true rating. The params, the error messages and the order of the arithmetic
// match Core's line for line, so both sides produce the same number. Both suites
// rate every day in testdata/fatigue-index/reference-days.json, a byte-identical
// copy pinned in scripts/ported-from-core.json.
//
// A day's schedule becomes five signals in [0,1]. They are weighted, scheduled
// rest is subtracted, and the sum is clamped: the patent's §18.2 day burden.
// §18.1's smoothstep then maps it onto the public 1-9 scale.
// ============================================================================

import { fiBand, type FIBand } from '@/app/utils/fi';

/** Names the formula version. Core returns its own with every rating; a
 *  different value means this port is out of date and Core's rating wins. */
export const INDEX_FORMULA = 'fi-2026-09-15';

export type ItemKind = 'activity' | 'travel' | 'rest';
export type TravelMode = 'flight' | 'transfer';
export type Intensity = 'chill' | 'balanced' | 'packed';

/** One scheduled thing on a day, in Core's wire shape. `start` and `end` are
 *  local "HH:MM"; an `end` earlier than `start` runs past midnight. */
export interface DayItem {
  kind: ItemKind;
  /** Travel only; absent means a transfer. */
  mode?: TravelMode;
  start: string;
  end: string;
  /** Activities only. */
  intensity?: Intensity;
}

/** Everything the formula reads for one date. */
export interface IndexDay {
  tz_shift_hours: number;
  days_since_shift: number;
  items: DayItem[];
}

export interface IndexFactors {
  activity: number;
  travel: number;
  jet_lag: number;
  sleep: number;
  recovery: number;
}

export interface DayRating {
  fi: number;
  band: FIBand;
  /** The clamped weighted sum the FI maps from. */
  burden: number;
  factors: IndexFactors;
}

/** The load each intensity adds per scheduled hour. Frozen on its own, because
 *  `Object.freeze` does not reach into a nested object: frozen only at the top,
 *  `INDEX_PARAMS.loadPerHour.chill = 1` would still retune every later rating. */
const LOAD_PER_HOUR: Readonly<Record<Intensity, number>> = Object.freeze({ chill: 0.35, balanced: 0.65, packed: 1.0 });

/** Core's DefaultIndexParams. Change both together, with INDEX_FORMULA.
 *  Frozen: it is the default argument of every rating, so a caller that edited a
 *  weight would change every later rating in the session, tests included. */
export const INDEX_PARAMS = Object.freeze({
  activityWeight: 0.95,
  travelWeight: 0.55,
  jetLagWeight: 0.35,
  sleepWeight: 0.3,
  recoveryCredit: 0.1,

  loadPerHour: LOAD_PER_HOUR,
  loadPerStop: 0.1625, // a quarter hour of balanced effort
  fullLoad: 11,

  fullTravelHours: 10,
  flightBeforeMinutes: 120,
  flightAfterMinutes: 30,

  shiftRecoveredPerDay: 1,
  fullShiftHours: 12,

  dayStartMinute: 8 * 60,
  dayEndMinute: 22 * 60,
  sleepNeedHours: 7, // the middle of a normal 6 to 8 hours
  fullSleepDeficitHours: 5,

  fullRestHours: 4,
});

const unit = (x: number): number => Math.min(1, Math.max(0, x));

/** "HH:MM" as minutes after midnight, refused in Core's words otherwise. */
function clockMinutes(clock: string): number {
  if (!/^\d\d:\d\d$/.test(clock)) throw new Error(`${JSON.stringify(clock)} is not HH:MM`);
  const hours = Number(clock.slice(0, 2));
  const minutes = Number(clock.slice(3));
  if (hours > 23 || minutes > 59) throw new Error(`${JSON.stringify(clock)} is not a time of day`);
  return hours * 60 + minutes;
}

/** Rate one day. Throws on a day it cannot read, naming the item, as Core does. */
export function rateDay(day: IndexDay, p = INDEX_PARAMS): DayRating {
  // Core decodes into a struct, so a missing field arrives as a zero and a
  // malformed one never arrives at all. Here the same payload can carry
  // anything, and an unchecked NaN spreads through every factor into `fi`, so
  // the shape is refused in the same voice Core uses for an item.
  if (!day || !Array.isArray(day.items)) throw new Error('a day needs an items array');
  if (!Number.isFinite(day.tz_shift_hours)) throw new Error('tz_shift_hours is not a number');
  if (!Number.isFinite(day.days_since_shift)) throw new Error('days_since_shift is not a number');
  if (day.days_since_shift < 0) throw new Error('days_since_shift is negative');

  let load = 0;
  let travelMinutes = 0;
  let restMinutes = 0;
  let minutesOutsideWindow = 0;
  day.items.forEach((item, i) => {
    if (!item) throw new Error(`item ${i}: no item`);
    let start: number;
    let end: number;
    try {
      start = clockMinutes(item.start);
    } catch (e) {
      throw new Error(`item ${i} start: ${(e as Error).message}`);
    }
    try {
      end = clockMinutes(item.end);
    } catch (e) {
      throw new Error(`item ${i} end: ${(e as Error).message}`);
    }
    if (end < start) end += 24 * 60;

    switch (item.kind) {
      case 'activity': {
        // An own key only, so an inherited one such as "toString" is refused too.
        if (item.intensity === undefined || !Object.prototype.hasOwnProperty.call(p.loadPerHour, item.intensity)) {
          throw new Error(`item ${i}: unknown intensity ${JSON.stringify(item.intensity ?? '')}`);
        }
        const perHour = p.loadPerHour[item.intensity];
        load += ((end - start) / 60) * perHour + p.loadPerStop;
        break;
      }
      case 'travel':
        if (item.mode === 'flight') {
          start -= p.flightBeforeMinutes;
          end += p.flightAfterMinutes;
        } else if (item.mode !== undefined && item.mode !== 'transfer') {
          throw new Error(`item ${i}: unknown travel mode ${JSON.stringify(item.mode)}`);
        }
        travelMinutes += end - start;
        break;
      case 'rest':
        restMinutes += end - start;
        break;
      default:
        throw new Error(`item ${i}: unknown kind ${JSON.stringify(item.kind)}`);
    }

    const early = Math.max(0, Math.min(end, p.dayStartMinute) - start);
    const late = Math.max(0, end - Math.max(start, p.dayEndMinute));
    minutesOutsideWindow += early + late;
  });

  const nightHours = (24 * 60 - p.dayEndMinute + p.dayStartMinute) / 60;
  const sleepHours = nightHours - minutesOutsideWindow / 60;
  const shiftLeft = Math.abs(day.tz_shift_hours) - p.shiftRecoveredPerDay * day.days_since_shift;

  const factors: IndexFactors = {
    activity: unit(load / p.fullLoad),
    travel: unit(travelMinutes / 60 / p.fullTravelHours),
    jet_lag: unit(shiftLeft / p.fullShiftHours),
    sleep: unit((p.sleepNeedHours - sleepHours) / p.fullSleepDeficitHours),
    recovery: unit(restMinutes / 60 / p.fullRestHours),
  };

  const burden = unit(
    p.activityWeight * factors.activity +
      p.travelWeight * factors.travel +
      p.jetLagWeight * factors.jet_lag +
      p.sleepWeight * factors.sleep -
      p.recoveryCredit * factors.recovery,
  );
  const smooth = 3 * burden * burden - 2 * burden * burden * burden;
  const fi = Math.max(1, Math.min(9, Math.floor(1 + 8 * smooth + 0.5)));

  return { fi, band: fiBand(fi), burden, factors };
}

/** A whole trip, rated from its days' ratings. */
export interface TripRating {
  fi: number;
  band: FIBand;
  typical_day: number;
  heaviest_day: number;
  packed_run: number;
  days: number;
}

/** The first rating in the Packed band, where a run of days starts compounding. */
export const PACKED_FROM = 7;

/**
 * Rate a trip from the ratings of its days — Core's `RateTrip`.
 *
 * The trip's rating is its typical day, not its average: a day's rating carries
 * travel honestly, so an average counts a long-haul flight in and out twice over
 * and a relaxed trip reads Packed. The heavy days are named separately (Brent's
 * decision). A run of packed days still compounds, as §18.2.1 describes: one
 * point at two in a row, one more at three.
 *
 * A rating outside the 1-9 scale is clamped on the way in, as Core does, so
 * every field comes from the same numbers.
 */
export function rateTrip(dayFIs: number[]): TripRating {
  // Refused the way rateDay refuses a day it cannot read, rather than surfacing
  // a raw TypeError from `.length` — this is exported, so the caller may not be
  // TypeScript.
  if (!Array.isArray(dayFIs)) throw new Error('a trip needs an array of day ratings');
  if (dayFIs.length === 0) return { fi: 1, band: fiBand(1), typical_day: 0, heaviest_day: 0, packed_run: 0, days: 0 };

  const days = dayFIs.map((fi, i) => {
    if (!Number.isFinite(fi)) throw new Error(`day ${i}: rating is not a number`);
    // Core's RateTrip takes []int, so a fraction cannot even be expressed there.
    // Refusing it here keeps the two sides saying the same thing, and keeps this
    // file's habit of refusing what it cannot read rather than rating it: a 7.8
    // would otherwise reach typical_day, heaviest_day and fi, none of which is a
    // value on the 1-9 scale.
    if (!Number.isInteger(fi)) throw new Error(`day ${i}: rating is not a whole number`);
    return Math.max(1, Math.min(9, fi));
  });
  const sorted = [...days].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  // Two middles: their average, rounded up, so an even-length trip leans to the
  // heavier of the pair rather than flattering itself.
  const typical = sorted.length % 2 === 1 ? sorted[mid] : Math.floor((sorted[mid - 1] + sorted[mid] + 1) / 2);

  let run = 0;
  let longestRun = 0;
  for (const fi of days) {
    run = fi >= PACKED_FROM ? run + 1 : 0;
    longestRun = Math.max(longestRun, run);
  }

  const fi = Math.max(1, Math.min(9, typical + (longestRun >= 2 ? 1 : 0) + (longestRun >= 3 ? 1 : 0)));
  return { fi, band: fiBand(fi), typical_day: typical, heaviest_day: sorted[sorted.length - 1], packed_run: longestRun, days: days.length };
}
